import React, { useCallback, useContext, useEffect, useMemo, useState } from "react"
import EventSource, { EventSourceListener } from "react-native-sse"
import { Children } from "../miscellianous/children"
import { EventNotification } from "../../core/dto/EventNotification/EventNotification"
import { getEnvProperty, PROPERTIES } from "../../core/services/environment-service"
import { appConstants } from "../../core/appConstants"
import { TokenContext } from "../../core/context/user/token-context"
import { useEvents } from "../../core/hooks/use-events"
import { EventTypeEnum } from "../../core/dto/EventNotification/EventTypeEnum"
import { ProjectListContext } from "../../core/context/project/ProjectListContext"

type MyCustomEvents = "EVENT"

export const NotificationContext = React.createContext<NotificationStore>({} as NotificationStore)

export function NotificationContextProvider({ children }: Readonly<Children>): React.JSX.Element {
  const { fetchUserNotifications, markEventAsRead, markAllEventAsRead } = useEvents()

  const { tokenRef } = useContext(TokenContext)
  const { refreshAllProjectCard } = useContext(ProjectListContext)

  const [eventList, setEventList] = useState<EventNotification[]>([])
  const [notification, setNotification] = useState<Notification | undefined>(undefined)

  useEffect(() => {
    fetchUserNotifications().then((list: EventNotification[]) => {
      const sortedList: EventNotification[] = [...list]
      sortedList.sort((a, b) => {
        if (a.createdDate && b.createdDate) {
          return b.createdDate.localeCompare(a.createdDate)
        }
        return 0
      })

      setEventList(sortedList)
    })
  }, [fetchUserNotifications])

  useEffect(() => {
    const url = `${getEnvProperty(PROPERTIES.REACT_APP_API_URL)}${appConstants.apiEndpoints.EVENT}/subscribe-event`

    const es = new EventSource<MyCustomEvents>(url, {
      debug: true,
      timeoutBeforeConnection: 10000,
      pollingInterval: 300,
      headers: {
        Authorization: {
          toString() {
            return `Bearer ${tokenRef.current}`
          },
        },
      },
    })

    const listener: EventSourceListener<MyCustomEvents> = (event) => {
      if (event.type === "open") {
        console.info("Open SSE connection.")
      } else if (event.type === "EVENT" && event.data) {
        const eventUpdate = JSON.parse(event.data) as EventNotification
        setEventList((array) => [eventUpdate, ...array])
        if (eventUpdate?.eventType === EventTypeEnum.PROJECT_INVITATION) {
          refreshAllProjectCard()
        }
      } else if (event.type === "error") {
        console.error("Connection error:", event.message)
      } else if (event.type === "exception") {
        console.error("Error:", event.message, event.error)
      }
    }

    es.addEventListener("open", listener)
    es.addEventListener("message", listener)
    es.addEventListener("error", listener)
    es.addEventListener("EVENT", listener)

    return () => {
      es.close()
    }
  }, [tokenRef])

  const handleMarkEventButton: (eventId: string) => void = useCallback(
    (eventId) => {
      markEventAsRead(eventId).then(() => {
        const indexToRemove = eventList.findIndex((item) => item.id === eventId)
        const newItems = eventList.filter((item, index) => index !== indexToRemove)
        setEventList(newItems)
      })
    },
    [eventList, markEventAsRead]
  )

  const handleMarkAllEventButton: () => void = useCallback(() => {
    markAllEventAsRead().then(() => {
      setEventList([])
    })
  }, [markAllEventAsRead])

  const notificationStore: NotificationStore = useMemo(
    () => ({ eventList, notification, handleMarkEventButton, handleMarkAllEventButton }),
    [eventList, notification, handleMarkEventButton, handleMarkAllEventButton]
  )

  return <NotificationContext.Provider value={notificationStore}>{children}</NotificationContext.Provider>
}

export interface NotificationStore {
  eventList: EventNotification[]
  notification: Notification | undefined
  handleMarkEventButton(eventId: string): void
  handleMarkAllEventButton(): void
}
